/**
 * TaskPauseReasonDialog - Dialog para informar motivo da pausa
 * 
 * Exibe campo textarea opcional para o motivo da pausa.
 * Estilo amber/yellow conforme especificado no plano.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial
 */

import { useState } from 'react';
import { Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Props do dialog de motivo da pausa
 */
interface TaskPauseReasonDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para abrir/fechar */
  onOpenChange: (open: boolean) => void;
  /** Callback ao confirmar pausa (com motivo opcional) */
  onConfirm: (reason?: string) => Promise<void>;
  /** Se está processando */
  isLoading?: boolean;
}

/**
 * Dialog para informar motivo da pausa
 */
export function TaskPauseReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: TaskPauseReasonDialogProps) {
  const [reason, setReason] = useState('');

  /** Handler de confirmação */
  const handleConfirm = async () => {
    await onConfirm(reason.trim() || undefined);
    setReason('');
    onOpenChange(false);
  };

  /** Handler de cancelamento */
  const handleCancel = () => {
    setReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <Pause className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            Pausar Tarefa
          </DialogTitle>
          <DialogDescription className="text-sm">
            Informe o motivo da pausa para melhor controle de produtividade.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Motivo da Pausa
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: Aguardando aprovação do cliente..."
            rows={3}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground">
            Este campo é opcional, mas ajuda no controle de produtividade.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {isLoading ? 'Pausando...' : 'Pausar Tarefa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
